import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import {
  Icon,
  Label,
  LabeledControl,
  List,
  Paragraph,
  Switch,
  TextButton,
  Toast,
  UpsellBanner,
  useToast,
} from '@zillow/constellation-10';
import { IconTagDollarSignFilled } from '@zillow/constellation-icons';

import { useRentalManagerUserPropertyEditWebApiHandler_displayTotalMonthlyPriceWithFee } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useGetListingDetails } from '../../hooks/queries/properties';
import { usePropertyEditMutation } from '../../hooks/usePropertyEditMutation';

import {
  emitLearnMoreEvent,
  emitOptInOffToOnEvent,
  emitOptInOnToOffEvent,
  EVENT_IDS,
} from '../../analytics/events/singleFamilyTotalMonthlyPrice';
import { useAuth } from '../AuthProvider';

import type { ChangeEvent } from 'react';

type Props = {
  triggerLocation: string;
};

const SFCostAndFeeOptInTotalMonthlyPriceWithFeeAlert = ({ triggerLocation }: Props) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;

  const { user } = useAuth();
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const displayTotalMonthlyPriceWithFee = listingDetails?.displayTotalMonthlyPriceWithFee ?? false;

  const [localDisplayTotalMonthlyPriceWithFee, setLocalDisplayTotalMonthlyPriceWithFee] = useState<boolean>(false);

  useEffect(() => {
    setLocalDisplayTotalMonthlyPriceWithFee(displayTotalMonthlyPriceWithFee);
  }, [displayTotalMonthlyPriceWithFee]);

  const { enqueueToast } = useToast();

  const { mutateAsync: setDisplayTotalMonthlyPriceWithFee, isPending: isSubmitting } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_displayTotalMonthlyPriceWithFee,
    skipToast: true,
  });

  const onChangeSwitchHandler = async (event: ChangeEvent<HTMLInputElement>) => {
    const valueToBe = event.target.checked;
    const previousValue = localDisplayTotalMonthlyPriceWithFee;

    setLocalDisplayTotalMonthlyPriceWithFee(valueToBe);

    if (listingDetails && user) {
      if (valueToBe) {
        emitOptInOffToOnEvent({ currentProperty: listingDetails, user, triggerLocation });
      } else {
        emitOptInOnToOffEvent({ currentProperty: listingDetails, user, triggerLocation });
      }
    }

    try {
      await setDisplayTotalMonthlyPriceWithFee({
        getParams: { listingAlias, displayTotalMonthlyPriceWithFee: valueToBe },
      });
    } catch {
      // Rollback on error
      setLocalDisplayTotalMonthlyPriceWithFee(previousValue);
      enqueueToast(<Toast tone="critical">This setting could not be saved. Refresh the page and try again.</Toast>);
    }
  };

  return (
    <UpsellBanner.Root
      tone="soft"
      data-testid="SFCostAndFeeOptInTotalMonthlyPriceWithFeeAlert"
      css={{
        alignItems: 'start',
        justifyContent: 'space-between',
        marginBottom: '400',
        overflow: 'hidden',
        borderRadius: 'obj.default',
      }}
    >
      <UpsellBanner.Media>
        <Icon render={<IconTagDollarSignFilled />} />
      </UpsellBanner.Media>

      <UpsellBanner.Content>
        <UpsellBanner.Header>
          <LabeledControl
            label={<Label css={{ textStyle: 'body-lg-bold !important' }}>Show the total price on your listing</Label>}
            control={
              <Switch
                data-testid="display-total-monthly-price-with-fee-switch"
                size="md"
                disabled={isSubmitting}
                checked={localDisplayTotalMonthlyPriceWithFee}
                onChange={onChangeSwitchHandler}
                data-eventid={displayTotalMonthlyPriceWithFee ? EVENT_IDS.OPT_IN_ON_TO_OFF : EVENT_IDS.OPT_IN_OFF_TO_ON}
              />
            }
            css={{ flexDirection: 'row-reverse', justifyContent: 'space-between' }}
          />
          <Paragraph>Turn this on if:</Paragraph>
          <List.Root appearance="bullet">
            <List.Item css={{ paddingBlock: 0 }}>
              Your base rent already includes all required monthly fees, or
            </List.Item>

            <List.Item css={{ paddingBlock: 0 }}>
              You&apos;re adding all required monthly fees here — we&apos;ll add them to the base rent to show one total
              monthly price
            </List.Item>
          </List.Root>
          <TextButton
            asChild
            css={{ marginTop: '400' }}
            onClick={() => {
              if (listingDetails && user) {
                emitLearnMoreEvent({
                  currentProperty: listingDetails,
                  user,
                  triggerObject: 'costs_and_fees',
                  triggerLocation,
                });
              }
            }}
            data-eventid={EVENT_IDS.LEARN_MORE}
          >
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://www.zillow.com/rentals-network/pricing-transparency/"
            >
              Learn more
            </a>
          </TextButton>
        </UpsellBanner.Header>
      </UpsellBanner.Content>
    </UpsellBanner.Root>
  );
};

export default SFCostAndFeeOptInTotalMonthlyPriceWithFeeAlert;
